import { useTranslation } from "@/contexts/LanguageContext";
import { Camera } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function PrivacyPage() {
  const { lang } = useTranslation();
  const contactEmail = "dslradvisor@gmail.com";

  const en = (
    <div className="space-y-6 text-[#D4D4D8] leading-relaxed text-[15px]">
      <p className="text-[#A1A1AA]">
        <strong className="text-white">Effective date:</strong> April 28, 2026
      </p>

      <p>
        DSLR Advisor (&quot;we&quot;, &quot;our&quot;, or &quot;the app&quot;)
        is operated by Hector Rivera. This Privacy Policy explains what
        information we collect, how we use it, and your rights.
      </p>

      <Section title="1. Information we collect">
        <ul className="list-disc ml-5 space-y-1.5">
          <li>
            <strong className="text-white">Account information.</strong> Email
            address and an encrypted password hash when you create an account.
          </li>
          <li>
            <strong className="text-white">App content you create.</strong> Your
            saved manual-settings history, gear inventory, and shopping list.
            This is associated with your account and only visible to you.
          </li>
          <li>
            <strong className="text-white">Location data.</strong> Optional
            geolocation if you allow it for the Golden Hour page. We do
            <strong> not </strong>
            store this data on our servers — it stays on your device.
          </li>
        </ul>
      </Section>

      <Section title="2. How we use the information">
        <ul className="list-disc ml-5 space-y-1.5">
          <li>To authenticate you and keep your saved data private.</li>
          <li>
            To personalize content (e.g., remember your camera, save your
            gear list).
          </li>
          <li>
            To improve the app — we do not sell or share your data with
            advertisers or third parties.
          </li>
        </ul>
      </Section>

      <Section title="3. Cookies and local storage">
        We use browser cookies and <code>localStorage</code> to keep you logged
        in and remember preferences (language, recently-searched cities, dark
        theme). No tracking or advertising cookies are used.
      </Section>

      <Section title="4. Third-party services">
        <ul className="list-disc ml-5 space-y-1.5">
          <li>
            <strong className="text-white">Open-Meteo Geocoding API.</strong>
            When you search a city on the Golden Hour page, we send your search
            term to Open-Meteo to find matching coordinates. They do not
            receive any personal information.
          </li>
        </ul>
      </Section>

      <Section title="5. Data retention">
        Your account and content remain in our database until you ask us to
        delete them. Email{" "}
        <a
          href={`mailto:${contactEmail}`}
          className="text-[#F5A524] underline"
        >
          {contactEmail}
        </a>{" "}
        and we will delete your account within 30 days.
      </Section>

      <Section title="6. Children's privacy">
        DSLR Advisor is intended for users aged 13 and older. We do not
        knowingly collect data from children under 13.
      </Section>

      <Section title="7. Security">
        Passwords are hashed with industry-standard bcrypt. Authentication is
        protected with JWT tokens. All traffic to our servers uses HTTPS.
      </Section>

      <Section title="8. Changes to this policy">
        We may update this policy from time to time. The &quot;Effective
        date&quot; above will reflect the latest revision. Material changes
        will be announced in the app's &quot;What&apos;s New&quot; panel.
      </Section>

      <Section title="9. Contact">
        Questions? Email{" "}
        <a
          href={`mailto:${contactEmail}`}
          className="text-[#F5A524] underline"
        >
          {contactEmail}
        </a>
        .
      </Section>
    </div>
  );

  const es = (
    <div className="space-y-6 text-[#D4D4D8] leading-relaxed text-[15px]">
      <p className="text-[#A1A1AA]">
        <strong className="text-white">Fecha de vigencia:</strong> 28 de abril
        de 2026
      </p>
      <p>
        DSLR Advisor (&quot;nosotros&quot;, &quot;nuestro&quot;, o &quot;la
        aplicacion&quot;) es operada por Hector Rivera. Esta Politica de
        Privacidad explica que informacion recopilamos, como la usamos y tus
        derechos.
      </p>

      <Section title="1. Informacion que recopilamos">
        <ul className="list-disc ml-5 space-y-1.5">
          <li>
            <strong className="text-white">Informacion de cuenta.</strong>{" "}
            Direccion de correo electronico y contrasena encriptada al crear
            una cuenta.
          </li>
          <li>
            <strong className="text-white">Contenido que creas en la app.</strong>{" "}
            Tu historial de ajustes manuales, inventario de equipo y lista de
            compras. Solo es visible para ti.
          </li>
          <li>
            <strong className="text-white">Datos de ubicacion.</strong>{" "}
            Geolocalizacion opcional si la permites en la pagina Hora Dorada.
            <strong> No </strong>
            almacenamos estos datos en nuestros servidores — quedan en tu
            dispositivo.
          </li>
        </ul>
      </Section>

      <Section title="2. Como usamos la informacion">
        <ul className="list-disc ml-5 space-y-1.5">
          <li>Para autenticarte y mantener privados tus datos guardados.</li>
          <li>Para personalizar contenido (recordar tu camara, lista de equipo).</li>
          <li>
            Para mejorar la app — no vendemos ni compartimos tus datos con
            anunciantes o terceros.
          </li>
        </ul>
      </Section>

      <Section title="3. Cookies y almacenamiento local">
        Usamos cookies y <code>localStorage</code> para mantenerte conectado y
        recordar preferencias. No se usan cookies de seguimiento ni
        publicitarias.
      </Section>

      <Section title="4. Servicios de terceros">
        <ul className="list-disc ml-5 space-y-1.5">
          <li>
            <strong className="text-white">API de Geocodificacion Open-Meteo.</strong>{" "}
            Cuando buscas una ciudad en la pagina Hora Dorada, enviamos tu
            termino de busqueda a Open-Meteo para encontrar coordenadas. No
            reciben informacion personal.
          </li>
        </ul>
      </Section>

      <Section title="5. Retencion de datos">
        Tu cuenta y contenido permanecen hasta que pidas eliminarlos. Escribe a{" "}
        <a
          href={`mailto:${contactEmail}`}
          className="text-[#F5A524] underline"
        >
          {contactEmail}
        </a>{" "}
        y eliminaremos tu cuenta en un plazo de 30 dias.
      </Section>

      <Section title="6. Privacidad infantil">
        DSLR Advisor esta destinado a usuarios de 13 anos en adelante. No
        recopilamos datos a sabiendas de menores de 13 anos.
      </Section>

      <Section title="7. Seguridad">
        Las contrasenas se cifran con bcrypt estandar. La autenticacion usa
        tokens JWT. Todo el trafico va por HTTPS.
      </Section>

      <Section title="8. Cambios a esta politica">
        Podemos actualizar esta politica periodicamente. La &quot;Fecha de
        vigencia&quot; reflejara la ultima revision. Los cambios materiales se
        anunciaran en el panel &quot;Novedades&quot; de la app.
      </Section>

      <Section title="9. Contacto">
        Preguntas? Escribe a{" "}
        <a
          href={`mailto:${contactEmail}`}
          className="text-[#F5A524] underline"
        >
          {contactEmail}
        </a>
        .
      </Section>
    </div>
  );

  return (
    <div
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
      data-testid="privacy-page"
    >
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white text-sm mb-5"
        data-testid="privacy-back-link"
      >
        <Camera weight="fill" className="w-4 h-4 text-[#F5A524]" />
        DSLR Advisor
      </Link>
      <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold text-white tracking-tight mb-6">
        {lang === "es" ? "Politica de Privacidad" : "Privacy Policy"}
      </h1>
      {lang === "es" ? es : en}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-white font-['Outfit'] text-lg font-medium mb-2">
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}
